'use client'

// libraries
import clsx from 'clsx'
import { useState, createContext, useContext, useEffect, useId } from 'react'
import dayjs from 'dayjs'

// components
import RichText from '/react4xp/components/RichText'
import Image from '/react4xp/components/Image'
import Link from '/react4xp/components/Link'

// utils
import { firstChar } from '/react4xp/utils/functions'

// css
import './index.scss'

// context
interface AccordionContextType {
    activeAccordionId: string | number | null
    setActiveAccordion: (id: string | number | null) => void
    oneAtTime: boolean
    expandAll: boolean | null
    resetExpandAll: () => void
}

export interface PublicationData {
    date: {
        label: string
        date: string
    }
    link: {
        href: string
        label: string
    }
    authors: {
        name: string
        link: {
            href: string
            external: boolean
            label: string
        }
        image?: {
            src: string
            alt: string
        }
    }[]
}

const AccordionContext = createContext<AccordionContextType | null>(null)

// types
export interface AccordionProps {
    className?: string
    anchorId?: string
    title?: string
    content?: string | React.ReactNode
    color?: 'green' | 'beige'
    onToggle?: () => void
    publicationData?: PublicationData
    defaultOpen?: boolean
}

export default function Accordion({
    className,
    anchorId,
    title,
    content,
    color = 'green',
    onToggle,
    publicationData,
    defaultOpen = false
}: AccordionProps) {
    const [internalIsActive, setInternalIsActive] = useState(defaultOpen)
    const context = useContext(AccordionContext)
    
    // generate unique id that's consistent between server and client
    const id = useId().replace(/:/g, '')
    
    // context state management
    const isActive = context?.oneAtTime 
        ? context.activeAccordionId === id
        : (context && context.expandAll !== null)
            ? context.expandAll 
            : internalIsActive
    
    // respond to expandAll changes
    useEffect(() => {
        if (context && !context.oneAtTime && context.expandAll !== null) {
            setInternalIsActive(context.expandAll)
        }
    }, [context])
    
    // toggle function
    const toggle = () => {
        // reset expandAll when clicking individual accordion
        if (context && context.expandAll !== null) {
            context.resetExpandAll()
        }
        
        if (context?.oneAtTime) {
            context.setActiveAccordion(isActive ? null : id)
        } else {
            setInternalIsActive((prev) => !prev)
        }
        onToggle?.()
    }

    useEffect(() => {
        if (context?.setActiveAccordion) {
            if (typeof window !== 'undefined' && anchorId) {
                const hash = window.location.hash.replace('#', '')
                if (hash && anchorId === hash) {
                    context.setActiveAccordion(id)
                    return
                }
            }
            // If defaultOpen is true and no other accordion is active, set this accordion as active
            if (defaultOpen && context.activeAccordionId === null) {
                context.setActiveAccordion(id)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Only run on mount

    return (
        <div className={clsx(
            'accordion',
            className,
            isActive && 'accordion--is-active',
            color === 'green' && 'accordion--green',
            color === 'beige' && 'accordion--beige',
            publicationData && 'accordion--publication'
        )} id={anchorId}>

            <button
                className='accordion__title h4'
                onClick={toggle}
                aria-expanded={isActive}
                aria-controls={`accordion-content-${id}`}
            >

                <span className='icon'>
                    <span />
                    <span />
                </span>

                <span className='accordion__title-text'>
                    <span>{title || ''}</span>

                    {publicationData?.authors && (
                        <span className='body-small'>
                            {publicationData.authors.map((author, i) => (
                                <span key={i}>
                                    {i > 0 && ', '}
                                    {author.name}
                                </span>
                            ))}
                        </span>
                    )}
                </span>

            </button>

            <div 
                className='accordion__content'
                id={`accordion-content-${id}`}
                aria-hidden={!isActive}
            >
                <div>

                    {publicationData?.date?.date && (
                        <div className='body'>
                            <p>{publicationData.date.label.replace('<<year>>', dayjs(publicationData.date.date).format('YYYY'))}</p>
                            <Link href={publicationData.link.href} isExternal={true} label={{ underline: publicationData.link.label }} />
                        </div>
                    )}

                    {typeof content === 'string' ? (
                        <RichText>
                            {content || ''}
                        </RichText>
                    ) : (
                        content
                    )}

                    {publicationData?.authors && (
                        <div className='accordion__authors'>
                            {publicationData.authors.filter(author => author.link?.href).map((author, i) => (
                                <div key={i} className='accordion__authors-item'>
                                    <div className='accordion__authors-item-image'>
                                        {
                                            author.image?.src && (
                                                <figure>
                                                    <Image
                                                        src={{
                                                            src: author.image?.src || '',
                                                            width: 128,
                                                            height: 128
                                                        }}
                                                        alt={author.image?.alt || ''}
                                                        className='cover'
                                                    />
                                                </figure>
                                            )
                                        }
                                        {
                                            !author.image?.src && (
                                                <p className='uppercase h2'>{firstChar(author.name || '')}</p>
                                            )
                                        }
                                    </div>
                                    <div>
                                        <p className='h3'>{author.name}</p>
                                        <Link
                                            href={author.link?.href}
                                            isExternal={author.link?.external}
                                            label={{ underline: author.link?.label }}
                                            className='body'
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

        </div>
    )
}

// types
interface AccordionListProps {
    className?: string
    children?: React.ReactNode
    oneAtTime?: boolean
    expandAllButton?: {
        expandLabel?: string
        collapseLabel?: string
    }
}

export function AccordionList({
    className,
    children,
    oneAtTime = false,
    expandAllButton
}: AccordionListProps) {

    const [activeAccordionId, setActiveAccordionId] = useState<string | number | null>(null)
    const [expandAll, setExpandAll] = useState<boolean | null>(null)

    const resetExpandAll = () => {
        setExpandAll(null)
    }

    const contextValue: AccordionContextType = {
        activeAccordionId,
        setActiveAccordion: setActiveAccordionId,
        oneAtTime,
        expandAll,
        resetExpandAll
    }

    const handleExpandAll = () => {
        setExpandAll((prev) => {
            if (prev === null) return true
            return !prev
        })
    }

    return (
        <AccordionContext.Provider value={contextValue}>

            {expandAllButton?.expandLabel && expandAllButton?.collapseLabel && (
                <button
                    className='accordion-list__expand-button button-hollow-black mb-2'
                    onClick={handleExpandAll}
                >
                    {expandAll ? expandAllButton.collapseLabel : expandAllButton.expandLabel}
                </button>
            )}

            <div className={clsx('accordion-list', className)}>
                {children}
            </div>

        </AccordionContext.Provider>
    )
}